import { Routes, Route, Navigate } from "react-router-dom";
import { MainLayout } from "./layouts/MainLayout";
import { SentinelAuthLayout } from "./layouts/SentinelAuthLayout";
import { IdentityProvider, useIdentity } from "./lib/identity";
import { useVisitorTracking } from "./lib/use-visitor-tracking";
import HomePage from "./pages/HomePage";
import IndustriesPage from "./pages/IndustriesPage";
import IndustryDetailPage from "./pages/IndustryDetailPage";
import ArchitecturePage from "./pages/ArchitecturePage";
import DomainsPage from "./pages/DomainsPage";
import DomainDetailPage from "./pages/DomainDetailPage";
import LicensingPage from "./pages/LicensingPage";
import VendorIntegrationPage from "./pages/VendorIntegrationPage";
import QuoteRequestPage from "./pages/QuoteRequestPage";
import PricingPage from "./pages/PricingPage";
import TrainingPage from "./pages/TrainingPage";
import CertificationPage from "./pages/CertificationPage";
import IndustryCertificationPage from "./pages/IndustryCertificationPage";
import CommunityPage from "./pages/CommunityPage";
import DownloadsPage from "./pages/DownloadsPage";
import ContactPage from "./pages/ContactPage";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import NotFoundPage from "./pages/NotFoundPage";
import DashboardPage from "./pages/DashboardPage";
import CertificationsCatalogPage from "./pages/CertificationsCatalogPage";
import MyProgramsPage from "./pages/MyProgramsPage";
import ProgramDetailPage from "./pages/ProgramDetailPage";
import LicensesPage from "./pages/LicensesPage";
import RenewalsPage from "./pages/RenewalsPage";
import CertificatesPage from "./pages/CertificatesPage";
import CapstonesPage from "./pages/CapstonesPage";
import BadgesPage from "./pages/BadgesPage";
import SeatsPage from "./pages/SeatsPage";
import AccountPage from "./pages/AccountPage";
import LogoutPage from "./pages/LogoutPage";
import AdminPage from "./pages/AdminPage";
import SeoAnalyticsPage from "./pages/SeoAnalyticsPage";
import { LepcApp } from "./lepc/LepcApp";
import PortalPage from "./pages/PortalPage";
import PortalRegisterPage from "./pages/PortalRegisterPage";
import PortalAdminPage from "./pages/PortalAdminPage";
import type { UserRole } from "./lib/certifications";
import type { ReactNode } from "react";

function RequireAuth({ children }: { children: ReactNode }) {
  const { identity, loading } = useIdentity();
  if (loading) return null;
  if (!identity.isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireRole({ children, roles }: { children: ReactNode; roles: UserRole[] }) {
  const { identity, loading, hasAnyRole } = useIdentity();
  if (loading) return null;
  if (!identity.isAuthenticated) return <Navigate to="/login" replace />;
  if (!hasAnyRole(roles)) return <NotFoundPage />;
  return <>{children}</>;
}

export default function App() {
  useVisitorTracking();
  return (
    <IdentityProvider>
      <Routes>
        {/* ── E Industries public website (MainLayout: E Industries nav + footer) ── */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/industries/:slug" element={<IndustryDetailPage />} />
          <Route path="/architecture" element={<ArchitecturePage />} />
          <Route path="/domains" element={<DomainsPage />} />
          <Route path="/domains/:slug" element={<DomainDetailPage />} />
          <Route path="/licensing" element={<LicensingPage />} />
          <Route path="/vendor-integration" element={<VendorIntegrationPage />} />
          <Route path="/licensing/quote" element={<QuoteRequestPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/training" element={<TrainingPage />} />
          <Route path="/certification" element={<CertificationPage />} />
          <Route path="/certifications/:industrySlug" element={<IndustryCertificationPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
        </Route>

        {/* ── Sentinel auth pages (SentinelAuthLayout: Sentinel branding only) ── */}
        <Route element={<SentinelAuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>

        {/* ── Sentinel platform pages (standalone — each page renders PlatformLayout internally) ── */}
        <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
        <Route path="/certifications" element={<RequireAuth><CertificationsCatalogPage /></RequireAuth>} />
        <Route path="/programs" element={<RequireAuth><MyProgramsPage /></RequireAuth>} />
        <Route path="/programs/:id" element={<RequireAuth><ProgramDetailPage /></RequireAuth>} />
        <Route path="/program/:id" element={<RequireAuth><ProgramDetailPage /></RequireAuth>} />
        <Route path="/licenses" element={<RequireAuth><LicensesPage /></RequireAuth>} />
        <Route path="/renewals" element={<RequireAuth><RenewalsPage /></RequireAuth>} />
        <Route path="/certificates" element={<RequireAuth><CertificatesPage /></RequireAuth>} />
        <Route path="/badges" element={<RequireAuth><BadgesPage /></RequireAuth>} />
        <Route path="/seats" element={<RequireRole roles={["agency_admin","enterprise_admin","platform_admin"]}><SeatsPage /></RequireRole>} />
        <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
        <Route path="/capstones" element={<RequireAuth><CapstonesPage /></RequireAuth>} />
        <Route path="/admin" element={<RequireRole roles={["platform_admin"]}><AdminPage /></RequireRole>} />
        <Route path="/admin/analytics" element={<RequireRole roles={["platform_admin"]}><SeoAnalyticsPage /></RequireRole>} />

        {/* ── Company Portal Pages ── */}
        <Route path="/portal/:companySlug" element={<PortalPage />} />
        <Route path="/portal/:companySlug/register" element={<PortalRegisterPage />} />
        <Route path="/portal/manage" element={<RequireRole roles={["enterprise_admin","agency_admin","platform_admin"]}><PortalAdminPage /></RequireRole>} />

        {/* ── LEP-C Professional Template System ── */}
        <Route path="/lepc/*" element={<LepcApp />} />

        {/* ── Sentinel auth actions ── */}
        <Route path="/logout" element={<LogoutPage />} />

        {/* ── 404 ── */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </IdentityProvider>
  );
}
